/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

// allow-any-unicode-comment-file
// DO NOT EDIT -- auto-generated by scripts/sync-agent-host-protocol.ts

// ─── Standard JSON-RPC Codes ─────────────────────────────────────────────────

/**
 * Standard JSON-RPC 2.0 error codes.
 *
 * @category Standard JSON-RPC Codes
 */
export const JsonRpcErrorCodes = {
	/** Invalid JSON */
	ParseError: -32700,
	/** Not a valid JSON-RPC request */
	InvalidRequest: -32600,
	/** Unknown method name */
	MethodNotFound: -32601,
	/** Invalid method parameters */
	InvalidParams: -32602,
	/** Unspecified server error */
	InternalError: -32603,
} as const;

// ─── AHP Application Codes ──────────────────────────────────────────────────

/**
 * AHP application-specific error codes.
 *
 * @category AHP Application Codes
 * @version 1
 */
export const AhpErrorCodes = {
	/** The referenced session URI does not exist */
	SessionNotFound: -32001,
	/** The requested agent provider is not registered */
	ProviderNotFound: -32002,
	/** A session with the given URI already exists */
	SessionAlreadyExists: -32003,
	/** The operation requires no active turn, but one is in progress */
	TurnInProgress: -32004,
	/** The client's protocol version is not supported by the server */
	UnsupportedProtocolVersion: -32005,
	/** The requested content URI does not exist */
	ContentNotFound: -32006,
	/**
	 * A command failed because the client has not authenticated for a required
	 * protected resource. The `data` field of the JSON-RPC error SHOULD contain
	 * an `IProtectedResourceMetadata[]` array describing the resources that
	 * require authentication.
	 *
	 * @see {@link /specification/authentication | Authentication}
	 */
	AuthRequired: -32007,
	/** The requested file, folder, or URI does not exist */
	NotFound: -32008,
	/**
	 * The client is not permitted to access the requested resource.
	 *
	 * Servers SHOULD return this when a client attempts to read or browse
	 * a path outside the allowed set (e.g. outside the session's working
	 * directory or workspace roots).
	 */
	PermissionDenied: -32009,
	/**
	 * The target resource already exists and the operation does not allow
	 * overwriting (e.g. `writeFile` with `createOnly: true`).
	 */
	AlreadyExists: -32010,
} as const;

/** Union type of all AHP application error codes. */
export type AhpErrorCode = (typeof AhpErrorCodes)[keyof typeof AhpErrorCodes];

/** Union type of all JSON-RPC error codes. */
export type JsonRpcErrorCode = (typeof JsonRpcErrorCodes)[keyof typeof JsonRpcErrorCodes];
